import { useState } from "react";
import { Button, Flex, ModalV2, Text, toast } from "@jsq/ds/core";
import {
  useDeleteComplianceDocumentMutation,
  useGetDocumentDetailsLazyQuery,
} from "~/graphql/supergraphHooks";
import { type BaseDocumentType } from "~/graphql/supergraphTypes";
import { DeleteSourceDocumentModal } from "../../../../DocumentActions/DeleteSourceDocumentModal";
import {
  deleteModalActions,
  deleteModalButton,
  deleteModalContent,
  documentsActionButton,
  documentsActionsGroup,
  documentsDownloadLabel,
  documentsDownloadPanel,
} from "./styles_vanilla.css";

type DocumentForPanel = {
  id: number;
  name?: string | null;
  profileId?: number | null;
  documentPurpose?: { baseType: BaseDocumentType } | null;
};

export type DeleteInfo = {
  showTaxDeleteModal: boolean;
  afterDeletionCB: () => void;
};

type Props = {
  caseId?: number;
  document?: DocumentForPanel;
  deleteInfo?: DeleteInfo;
};

export const DocumentsDownloadPanel = ({ caseId, document, deleteInfo }: Props) => {
  const documentId = document?.id;
  const documentName = document?.name;
  const documentProfileId = document?.profileId;
  const documentBaseType = document?.documentPurpose?.baseType;

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [taxDeleteModalOpen, setTaxDeleteModalOpen] = useState(false);

  const [getDocumentDetails, { loading }] = useGetDocumentDetailsLazyQuery({
    fetchPolicy: "network-only",
  });

  const [deleteComplianceDocument, { loading: deleteLoading }] =
    useDeleteComplianceDocumentMutation({
      refetchQueries: ["GetComplianceCase"],
      awaitRefetchQueries: true,
      onError: (e) => {
        toast.error(e instanceof Error ? e.message : String(e));
        throw e;
      },
    });

  const handleGoToDownloadPage = async () => {
    const { data } = await getDocumentDetails({
      variables: { documentId: documentId! },
    });
    window.open(data?.document?.s3Link);
  };

  const handleDeleteClick = () => {
    if (deleteInfo?.showTaxDeleteModal) {
      setTaxDeleteModalOpen(true);
    } else {
      setDeleteModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setDeleteModalOpen(false);
    setTaxDeleteModalOpen(false);
  };

  const handleDelete = async () => {
    if (documentId === undefined || caseId === undefined) return;
    try {
      await deleteComplianceDocument({
        variables: { caseId, documentId },
      });
      toast.success(`${documentName} has been successfully deleted`);
      handleCloseModal();
      deleteInfo?.afterDeletionCB();
    } catch {
      // Error already handled by onError
    }
  };

  return (
    <>
      <Flex
        className={documentsDownloadPanel}
        alignItems="center"
        justifyContent="space-between"
      >
        <Text className={documentsDownloadLabel}>{documentName}</Text>
        <Flex className={documentsActionsGroup}>
          <Button
            variant="neutral"
            icon="download"
            className={documentsActionButton}
            isLoading={loading}
            onClick={() => handleGoToDownloadPage()}
          >
            Download
          </Button>
          {deleteInfo != null && (
            <Button
              variant="neutral"
              icon="trash"
              className={documentsActionButton}
              onClick={handleDeleteClick}
            >
              Delete
            </Button>
          )}
        </Flex>
      </Flex>
      {taxDeleteModalOpen &&
        documentId !== undefined &&
        documentProfileId != null &&
        documentBaseType && (
          <DeleteSourceDocumentModal
            documentId={documentId}
            documentName={documentName ?? ""}
            documentType={documentBaseType}
            profileId={documentProfileId}
            onClose={handleCloseModal}
            onDelete={() => void handleDelete()}
            isDeleting={deleteLoading}
          />
        )}
      {deleteModalOpen && (
        <ModalV2
          show={true}
          onHide={handleCloseModal}
          isCentered
          backdrop
          title="Delete document"
        >
          <Flex flexDirection="column" className={deleteModalContent}>
            <Text variant="secondary-md" marginBottom="md">
              {documentName}
            </Text>
            <Text>Are you sure you want to delete this document?</Text>
          </Flex>
          <Flex justifyContent="flex-end" className={deleteModalActions}>
            <Button onClick={handleCloseModal} variant="neutral" className={deleteModalButton}>
              Cancel
            </Button>
            <Button
              isLoading={deleteLoading}
              onClick={() => void handleDelete()}
              variant="danger"
              className={deleteModalButton}
            >
              Delete document
            </Button>
          </Flex>
        </ModalV2>
      )}
    </>
  );
};
